import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { getTokens } from '../../lib/auth-storage';
import {
  createReferenceDetails,
  type ReferenceKind,
  type ReferenceDetail,
} from '../../lib/operations/reference-details.repository';
import { useRemote } from '../../lib/operations/use-remote';

const Context = createContext<ReturnType<typeof createReferenceDetails> | null>(null);
export function ReferenceDetailsProvider({
  children,
  revision = 0,
}: {
  children: ReactNode;
  revision?: number;
}) {
  const token = getTokens()?.accessToken;
  const cache = useMemo(() => {
    void revision;
    return createReferenceDetails(token);
  }, [token, revision]);
  return <Context.Provider value={cache}>{children}</Context.Provider>;
}

export function ReferenceDetails({
  kind,
  id,
  empty = 'Not recorded',
  scope,
  onResolved,
  unavailable,
}: {
  kind: ReferenceKind;
  scope?: string;
  id: string | null | undefined;
  empty?: string;
  onResolved?: (detail: ReferenceDetail) => void;
  unavailable?: string;
}) {
  const shared = useContext(Context);
  const token = getTokens()?.accessToken;
  const local = useMemo(() => createReferenceDetails(token), [token]);
  const cache = shared ?? local;
  const load = useCallback(() => cache.get(kind, id ?? '', scope), [cache, kind, id, scope]);
  const result = useRemote(load, Boolean(id));
  useEffect(() => {
    if (result.data && !result.error) onResolved?.(result.data);
  }, [result.data, result.error, onResolved]);
  if (!id) return <span>{empty}</span>;
  if (result.busy) return <span role="status">Loading details…</span>;
  if (!result.data || result.error)
    return (
      <span>
        {unavailable ?? (kind === 'account' || kind === 'employee' ? 'Name unavailable' : 'Details unavailable')}
      </span>
    );
  return result.data.href ? (
    <Link className="text-primary underline" to={result.data.href}>
      {result.data.label}
    </Link>
  ) : (
    <span>{result.data.label}</span>
  );
}
